import React from 'react';

interface UserAvatarProps {
  photoUrl?: string | null;
  emoji?: string | null;
  firstName?: string;
  lastName?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  onClick?: () => void;
  className?: string;
}

const SIZE_CLASSES = {
  sm: 'w-8 h-8 text-sm',
  md: 'w-10 h-10 text-base',
  lg: 'w-16 h-16 text-2xl',
  xl: 'w-28 h-28 text-4xl',
} as const;

const EMOJI_TEXT_SIZE = {
  sm: 'text-lg',
  md: 'text-xl',
  lg: 'text-3xl',
  xl: 'text-5xl',
} as const;

const UserAvatar: React.FC<UserAvatarProps> = ({
  photoUrl,
  emoji,
  firstName = '',
  lastName = '',
  size = 'md',
  onClick,
  className = '',
}) => {
  const sizeClass = SIZE_CLASSES[size];
  const emojiSize = EMOJI_TEXT_SIZE[size];
  const initials = `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase();
  const isClickable = !!onClick;

  const baseClasses = [
    sizeClass,
    'rounded-2xl flex items-center justify-center overflow-hidden shrink-0',
    isClickable ? 'cursor-pointer hover:ring-2 hover:ring-primary-300 transition-all' : '',
    className,
  ].join(' ');

  if (photoUrl && photoUrl !== 'pending-upload') {
    return (
      <div className={baseClasses} onClick={onClick} role={isClickable ? 'button' : undefined}>
        <img src={photoUrl} alt={`${firstName} ${lastName}`} className="w-full h-full object-cover" />
      </div>
    );
  }

  if (emoji) {
    return (
      <div
        className={`${baseClasses} bg-primary-50 border-2 border-primary-100`}
        onClick={onClick}
        role={isClickable ? 'button' : undefined}
      >
        <span className={emojiSize}>{emoji}</span>
      </div>
    );
  }

  return (
    <div
      className={`${baseClasses} bg-gradient-to-br from-primary-200 to-primary-400`}
      onClick={onClick}
      role={isClickable ? 'button' : undefined}
    >
      <span className="font-bold text-primary-700">{initials}</span>
    </div>
  );
};

export default UserAvatar;
